import { useEffect, useState } from "react";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Smartphone, Share, MoreVertical, Plus, CheckCircle2, Sparkles } from "lucide-react";

/**
 * Cross-platform "Install as App" walkthrough modal.
 *
 * • Auto-detects iOS vs Android and opens the correct tab first.
 * • Hides itself if the app is already running standalone (already installed).
 * • On Chrome/Android where `beforeinstallprompt` fires, exposes a one-tap
 *   native install button.
 * • On iOS Safari (no install API) — shows a clear illustrated walkthrough.
 */

// Custom hook exposed so callers can hide their trigger CTA when the PWA
// is already installed or beforeinstallprompt has been captured.
export function usePwaInstall() {
  const [deferredPrompt, setDeferredPrompt] = useState(null);
  const [standalone, setStandalone] = useState(false);

  useEffect(() => {
    // Detect standalone mode (already installed)
    const isStandalone =
      window.matchMedia("(display-mode: standalone)").matches ||
      window.navigator.standalone === true;
    setStandalone(isStandalone);

    const onPrompt = (e) => {
      e.preventDefault();
      setDeferredPrompt(e);
    };
    const onInstalled = () => setStandalone(true);

    window.addEventListener("beforeinstallprompt", onPrompt);
    window.addEventListener("appinstalled", onInstalled);
    return () => {
      window.removeEventListener("beforeinstallprompt", onPrompt);
      window.removeEventListener("appinstalled", onInstalled);
    };
  }, []);

  const triggerInstall = async () => {
    if (!deferredPrompt) return false;
    deferredPrompt.prompt();
    const choice = await deferredPrompt.userChoice;
    setDeferredPrompt(null);
    return choice.outcome === "accepted";
  };

  return { standalone, canPromptInstall: !!deferredPrompt, triggerInstall };
}

function detectPlatform() {
  const ua = navigator.userAgent || navigator.vendor || "";
  if (/iPad|iPhone|iPod/i.test(ua) && !window.MSStream) return "ios";
  if (/android/i.test(ua)) return "android";
  return "other";
}

export default function InstallAppModal({ open, onOpenChange }) {
  const [tab, setTab] = useState("android");
  const { canPromptInstall, triggerInstall } = usePwaInstall();

  useEffect(() => {
    if (open) setTab(detectPlatform() === "ios" ? "ios" : "android");
  }, [open]);

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent dir="rtl" className="sm:max-w-md p-0 overflow-hidden" data-testid="install-app-modal">
        <div className="p-5 sm:p-6 space-y-4">
          <DialogHeader className="space-y-1 text-start">
            <DialogTitle className="text-xl flex items-center gap-2">
              <Smartphone className="h-5 w-5 text-primary" />
              حمّل التطبيق على جوالك
            </DialogTitle>
            <p className="text-xs text-muted-foreground leading-relaxed">
              ثبّت المنصة كتطبيق على شاشتك الرئيسية للوصول السريع، إشعارات فورية،
              وتجربة استخدام بدون شريط المتصفح.
            </p>
          </DialogHeader>

          {/* One-tap install for Chrome/Android */}
          {canPromptInstall && (
            <button
              onClick={triggerInstall}
              className="w-full rounded-lg bg-gradient-to-br from-emerald-500 to-emerald-600 text-white p-3 flex items-center justify-between hover:brightness-110 transition"
              data-testid="btn-native-install"
            >
              <div className="text-start">
                <div className="font-semibold text-sm flex items-center gap-1.5">
                  <Sparkles className="h-4 w-4" /> تثبيت بضغطة واحدة
                </div>
                <div className="text-[11px] opacity-90 mt-0.5">
                  متصفحك يدعم التثبيت الفوري — اضغط هنا
                </div>
              </div>
              <Plus className="h-6 w-6" />
            </button>
          )}

          {/* Tabs */}
          <div role="tablist" className="inline-flex p-1 rounded-full bg-muted w-full">
            <button
              role="tab"
              aria-selected={tab === "android"}
              onClick={() => setTab("android")}
              className={`flex-1 px-4 py-2 rounded-full text-sm font-medium transition-all ${
                tab === "android" ? "bg-background shadow-sm" : "text-muted-foreground"
              }`}
              data-testid="tab-android"
            >
              أندرويد
            </button>
            <button
              role="tab"
              aria-selected={tab === "ios"}
              onClick={() => setTab("ios")}
              className={`flex-1 px-4 py-2 rounded-full text-sm font-medium transition-all ${
                tab === "ios" ? "bg-background shadow-sm" : "text-muted-foreground"
              }`}
              data-testid="tab-ios"
            >
              آيفون / آيباد
            </button>
          </div>

          {tab === "ios" ? <IOSSteps /> : <AndroidSteps />}

          <div className="rounded-lg bg-emerald-50 dark:bg-emerald-950/40 border border-emerald-200 dark:border-emerald-800 p-3 flex items-start gap-2">
            <CheckCircle2 className="h-4 w-4 text-emerald-600 dark:text-emerald-400 mt-0.5 shrink-0" />
            <p className="text-[11px] leading-relaxed text-emerald-900 dark:text-emerald-100">
              بعد التثبيت، سيظهر أيقونة التطبيق على شاشتك الرئيسية بشعار المدرب
              فيصل. افتحها كأي تطبيق عادي — لا حاجة لفتح المتصفح.
            </p>
          </div>

          <div className="text-center">
            <Button variant="ghost" size="sm" onClick={() => onOpenChange(false)} data-testid="btn-close-install">
              إغلاق
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}

function Step({ n, title, children, icon }) {
  return (
    <div className="flex gap-3">
      <div className="h-7 w-7 rounded-full bg-primary text-primary-foreground grid place-items-center font-bold text-xs shrink-0">
        {n}
      </div>
      <div className="flex-1 pb-1">
        <div className="text-sm font-semibold flex items-center gap-1.5">
          {icon}
          {title}
        </div>
        <div className="text-xs text-muted-foreground mt-0.5 leading-relaxed">{children}</div>
      </div>
    </div>
  );
}

function IOSSteps() {
  return (
    <div className="space-y-3" data-testid="steps-ios">
      <Step n={1} title="افتح الموقع في متصفح Safari">
        تأكد من فتح <span className="font-mono text-foreground">faisalfit.com</span> عبر Safari (وليس Chrome أو
        أي متصفح آخر — التثبيت مدعوم فقط في Safari على iPhone / iPad).
      </Step>
      <Step
        n={2}
        title={<>اضغط زر المشاركة <Share className="h-3.5 w-3.5 inline text-blue-500" /></>}
      >
        الأيقونة موجودة في شريط الأدوات أسفل الشاشة (مربع بسهم يتجه للأعلى).
      </Step>
      <Step n={3} title="اختر «إضافة إلى الشاشة الرئيسية»">
        اسحب القائمة قليلاً لأعلى إذا لم تظهر الخيار — تسميته بالإنجليزية:
        <span className="font-mono text-foreground"> Add to Home Screen</span>.
      </Step>
      <Step n={4} title="اضغط «إضافة» في أعلى اليمين">
        سيظهر أيقونة تطبيق «FaisalFit» على شاشتك الرئيسية فوراً.
      </Step>
    </div>
  );
}

function AndroidSteps() {
  return (
    <div className="space-y-3" data-testid="steps-android">
      <Step n={1} title="افتح الموقع في متصفح Chrome">
        الأفضل استخدام Chrome أو Edge أو Samsung Internet — كلها تدعم التثبيت.
      </Step>
      <Step
        n={2}
        title={<>اضغط قائمة الثلاث نقاط <MoreVertical className="h-3.5 w-3.5 inline" /></>}
      >
        الأيقونة موجودة في أعلى يمين شاشة المتصفح (⋮).
      </Step>
      <Step n={3} title="اختر «تثبيت التطبيق» أو «Install app»">
        قد تظهر أيضاً باسم «إضافة إلى الشاشة الرئيسية» —
        <span className="font-mono text-foreground"> Add to Home screen</span>. كلاهما يعمل بنفس الطريقة.
      </Step>
      <Step n={4} title="اضغط «تثبيت» عند ظهور النافذة">
        سيظهر أيقونة تطبيق «FaisalFit» في قائمة التطبيقات وعلى شاشتك الرئيسية.
      </Step>
    </div>
  );
}
